import {
  Box,
  Card,
  CardContent,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { formatMoney } from '../../../../../../../../lib/functions/utils';

const COLORS = ['#0F23DF', '#07D2FE', '#B8336A', '#FFC400', '#E8C547'];

interface IncomeBookData {
  date: string;
  totalWallet: number;
  totalWalletNet: number;
  totalPriceIncome: number;
  totalArrearIncome: number;
  totalComissionIncome: number;
  totalExpensesIncome: number;
  totalDocumentExtensionIncome: number;
  totalIncome: number;
  totalDocumentResellExpense: number;
}

interface IncomeBreakdownChartProps {
  data: IncomeBookData[];
  onClickExecutive: () => void;
  loading?: boolean;
}

const IncomeBreakdownChart: React.FC<IncomeBreakdownChartProps> = ({
  data,
  loading = false,
  onClickExecutive,
}) => {
  const [viewMode, setViewMode] = useState<'daily' | 'monthly'>('daily');
  const [chartData, setChartData] = useState<any[]>([]);
  const [totalIncome, setTotalIncome] = useState(0);

  // Determinar si se puede mostrar la vista mensual (necesita al menos 2 meses)
  const canShowMonthly = () => {
    if (!data || data.length === 0) return false;

    const uniqueMonths = new Set();
    data.forEach((item) => {
      const month = new Date(item.date).toISOString().substring(0, 7); // YYYY-MM
      uniqueMonths.add(month);
    });

    return uniqueMonths.size >= 2;
  };

  // Función para formatear fechas de manera más legible
  const formatDate = (dateString: string, isMonthly: boolean = false) => {
    // Crear la fecha considerando que viene en formato YYYY-MM-DD
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month - 1 porque los meses van de 0-11

    if (isMonthly) {
      return date.toLocaleDateString('es-CL', {
        year: 'numeric',
        month: 'long',
      });
    } else {
      return date.toLocaleDateString('es-CL', {
        day: '2-digit',
        month: '2-digit',
      });
    }
  };

  // Procesar datos para el gráfico
  const processChartData = () => {
    if (!data || data.length === 0) {
      setChartData([]);
      setTotalIncome(0);
      return;
    }

    let processedData: any[] = [];

    if (viewMode === 'daily') {
      // Agrupar por día
      const dailyMap = new Map();

      data.forEach((item) => {
        const date = formatDate(item.date, false);

        if (dailyMap.has(date)) {
          const existing = dailyMap.get(date);
          dailyMap.set(date, {
            name: date,
            totalPriceIncome: existing.totalPriceIncome + item.totalPriceIncome,
            totalArrearIncome:
              existing.totalArrearIncome + item.totalArrearIncome,
            totalComissionIncome:
              existing.totalComissionIncome + item.totalComissionIncome,
            totalExpensesIncome:
              existing.totalExpensesIncome + item.totalExpensesIncome,
            totalDocumentExtensionIncome:
              existing.totalDocumentExtensionIncome +
              item.totalDocumentExtensionIncome,
          });
        } else {
          dailyMap.set(date, {
            name: date,
            totalPriceIncome: item.totalPriceIncome,
            totalArrearIncome: item.totalArrearIncome,
            totalComissionIncome: item.totalComissionIncome,
            totalExpensesIncome: item.totalExpensesIncome,
            totalDocumentExtensionIncome: item.totalDocumentExtensionIncome,
          });
        }
      });

      processedData = Array.from(dailyMap.values());
    } else {
      // Agrupar por mes
      const monthlyMap = new Map();

      data.forEach((item) => {
        const month = formatDate(item.date, true);

        if (monthlyMap.has(month)) {
          const existing = monthlyMap.get(month);
          monthlyMap.set(month, {
            name: month,
            totalPriceIncome: existing.totalPriceIncome + item.totalPriceIncome,
            totalArrearIncome:
              existing.totalArrearIncome + item.totalArrearIncome,
            totalComissionIncome:
              existing.totalComissionIncome + item.totalComissionIncome,
            totalExpensesIncome:
              existing.totalExpensesIncome + item.totalExpensesIncome,
            totalDocumentExtensionIncome:
              existing.totalDocumentExtensionIncome +
              item.totalDocumentExtensionIncome,
          });
        } else {
          monthlyMap.set(month, {
            name: month,
            totalPriceIncome: item.totalPriceIncome,
            totalArrearIncome: item.totalArrearIncome,
            totalComissionIncome: item.totalComissionIncome,
            totalExpensesIncome: item.totalExpensesIncome,
            totalDocumentExtensionIncome: item.totalDocumentExtensionIncome,
          });
        }
      });

      processedData = Array.from(monthlyMap.values());
    }

    setChartData(processedData);
  };

  useEffect(() => {
    processChartData();
  }, [data, viewMode]);

  // Calcular income breakdown usando useMemo para evitar re-renders infinitos
  const incomeBreakdown = React.useMemo(() => {
    if (!data || data.length === 0) {
      setTotalIncome(0);
      return [];
    }

    const totals = data.reduce(
      (acc, item) => ({
        totalPriceIncome: acc.totalPriceIncome + item.totalPriceIncome,
        totalArrearIncome: acc.totalArrearIncome + item.totalArrearIncome,
        totalComissionIncome:
          acc.totalComissionIncome + item.totalComissionIncome,
        totalExpensesIncome: acc.totalExpensesIncome + item.totalExpensesIncome,
        totalDocumentExtensionIncome:
          acc.totalDocumentExtensionIncome + item.totalDocumentExtensionIncome,
      }),
      {
        totalPriceIncome: 0,
        totalArrearIncome: 0,
        totalComissionIncome: 0,
        totalExpensesIncome: 0,
        totalDocumentExtensionIncome: 0,
      },
    );

    const total = Object.values(totals).reduce((sum, value) => sum + value, 0);
    setTotalIncome(total);

    // Filtrar solo los ingresos que tienen valor
    const breakdown = [
      { name: 'Devengo', value: totals.totalPriceIncome },
      { name: 'Mora', value: totals.totalArrearIncome },
      { name: 'Comisión', value: totals.totalComissionIncome },
      { name: 'Gastos', value: totals.totalExpensesIncome },
      { name: 'Prorroga', value: totals.totalDocumentExtensionIncome },
    ].filter((item) => item.value > 0);

    return breakdown;
  }, [data]);

  return (
    <Card sx={{ height: '100%', minHeight: 300 }}>
      <CardContent>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" component="h2">
            Ingresos
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            style={{ cursor: 'pointer', color: 'blue' }}
            onClick={onClickExecutive}
          >
            Por ejecutivo
          </Typography>
        </div>

        {/* Gráfico de dona cortado por la mitad */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={200}
        >
          {loading ? (
            <Typography>Cargando datos...</Typography>
          ) : incomeBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={incomeBreakdown}
                  cx="50%"
                  cy="60%"
                  startAngle={180}
                  endAngle={0}
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  strokeWidth={0}
                  stroke="#fff"
                  cornerRadius={1}
                >
                  {incomeBreakdown.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload?.length) {
                      const data = payload[0].payload;
                      const percentage = (
                        (data.value / totalIncome) *
                        100
                      ).toFixed(1);
                      return (
                        <Box
                          sx={{
                            backgroundColor: 'white',
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            p: 1.5,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                          >
                            {data.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatMoney(data.value)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {percentage}% del total
                          </Typography>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Typography color="textSecondary">
              No hay datos disponibles para mostrar
            </Typography>
          )}
        </Box>

        {/* Leyenda */}
        {incomeBreakdown.length > 0 && (
          <Box
            display="flex"
            flexWrap="wrap"
            gap={0.5}
            mt={1.5}
            justifyContent="center"
          >
            {incomeBreakdown.map((entry, index) => (
              <Box
                key={entry.name}
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  backgroundColor: COLORS[index % COLORS.length],
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 2,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    backgroundColor: 'white',
                    borderRadius: '50%',
                  }}
                />
                {entry.name}
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default IncomeBreakdownChart;
